import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Calendar } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  "seguro-auto": "Seguro Auto",
  "seguro-empresarial": "Seguro Empresarial",
  "seguro-vida": "Seguro de Vida",
  "seguro-residencial": "Seguro Residencial",
  "seguro-viagem": "Seguro Viagem",
  "seguro-cyber": "Riscos Cibernéticos",
  "geral": "Dicas de Seguros",
};

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => {
      setArticles(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="pt-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <BookOpen className="h-4 w-4 text-gold" />
            <span className="text-sm text-white/80 font-medium">Blog</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-4">
            Dicas de <span className="text-gold">Seguros</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Artigos e dicas para ajudar você a entender melhor o mundo dos seguros e tomar as melhores decisões.
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-20 mb-4" />
                  <div className="h-5 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-5 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-slate-100 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-5/6" />
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-500">Nenhum artigo publicado ainda.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link key={article.id} to={`/blog/${article.slug}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`blog-card-${article.slug}`}>
                  {article.image_url && (
                    <img
                      src={article.image_url.startsWith("http") ? article.image_url : `${process.env.REACT_APP_BACKEND_URL}${article.image_url}`}
                      alt={article.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-semibold bg-brand/10 text-brand px-2 py-0.5 rounded-full uppercase">{categoryLabels[article.category] || article.category}</span>
                      <span className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Calendar className="h-3 w-3" />
                        {new Date(article.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-navy text-lg mb-2 group-hover:text-brand transition-colors leading-snug">{article.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
                    <span className="text-sm font-medium text-brand flex items-center gap-1">
                      Ler artigo <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
